import { OCTOLogo } from "assets";
import { AddFillIcon, CloseIcon, HambugerIcon, LeftIcon } from "assets/icons";
import Divider from "components/foundation/Divider";
import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import GnbItemProfile from "./GnbItemProfile";
import useMyDeskNotificationQuery from "features/my-page/queries/useMyDeskNotificationQuery";
import useUserDetailQuery from "features/user/hooks/queries/useUserDetailQuery";
import { ROUTER_PATH } from "config/path";
import { Link, useLocation, useMatches, useNavigate } from "react-router-dom";
import Modal from "components/foundation/Modal/Modal";
import { useLogoutMutation } from "features/auth/hooks/mutations";
import { useSetRecoilState } from "recoil";
import { isLoggedOutState } from "store/auth";
import { useQueryClient } from "@tanstack/react-query";
import api, { logout } from "apis";
import NewBadge from "./NewBadge";
import WithDrawModal from "components/myInfo/WithDrawModal/WithDrawModal";
import BottomSheet from "components/foundation/BottomSheet/BottomSheet";
import {
  ADMIN_TYPES,
  DOCUMENT_CATEGORY_UUID,
  LOCAL_TIME_FORMAT,
} from "config/constant";
import { MatchHandle } from "models/MatchHandleType";
import dayjs from "dayjs";
import { useGetUnreadPushNotificationCount } from "features/pushNotification/queries";
import GnbPushNotification from "./GnbPushNotification";

const UPLOAD_OPTIONS = [
  {
    label: "WORK",
    to: ROUTER_PATH.document.work.create._path,
  },
  {
    label: "ANALYSIS",
  },
  {
    label: "ARCHIVES",
  },
  {
    label: "NOTICE",
    to: ROUTER_PATH.notice.create,
  },
];

const GNB_PATHS = [
  ROUTER_PATH.home,
  ROUTER_PATH.myPage.information,
  ROUTER_PATH.myPage.desk,
  ROUTER_PATH.document.work.dashboard,
  ROUTER_PATH.document.analysis.dashboard,
  ROUTER_PATH.document.archive.dashboard,
  ROUTER_PATH.notice.dashboard._path,
  ROUTER_PATH.user.dashboard._path,
  ROUTER_PATH.tag.dashboard._path,
];

const GNB_WITH_TITLE_PATHS = [
  { route: ROUTER_PATH.myPage.information, title: "MY INFORMATION" },
  { route: ROUTER_PATH.pushNotification._path, title: "알림" },
];

const GnbHeaderMobile = () => {
  /* 현재 경로 확인 */
  const location = useLocation();
  const showMobileGnb = GNB_PATHS.includes(location.pathname);
  const showGnbWithTitle = GNB_WITH_TITLE_PATHS.some(({ route }) =>
    location.pathname.includes(route),
  );

  /* 페이지 제목 */
  const matches = useMatches();
  const match = matches[matches.length - 1];
  const handle = match?.handle as MatchHandle;

  /* 유저 프로필 */
  const {
    data: currentUser,
    isInitialLoading: isUserInitalLoading,
    isSuccess: isUserSuccess,
  } = useUserDetailQuery();

  const CURRENT_DATE = useMemo(() => dayjs().format(LOCAL_TIME_FORMAT), []);

  const { data: hasNewMyDeskInfo } = useMyDeskNotificationQuery(
    {
      lastCheckedAt: CURRENT_DATE,
    },
    isUserSuccess,
  );

  const notNormalUser = !!currentUser?.authorityDto;

  /* 메뉴 */
  // Upload 메뉴
  const [openUploadBottomSheet, setOpenUploadBottomSheet] = useState(false);

  // USER 메뉴
  const [openUserBottomSheet, setOpenUserBottomSheet] = useState(false);

  const isAdmin = useMemo(() => {
    return (
      currentUser?.adminType && ADMIN_TYPES.includes(currentUser.adminType)
    );
  }, [currentUser]);

  const isRegularMember = useMemo(() => {
    return currentUser?.regularMember;
  }, [currentUser]);

  const userMenu = [
    {
      label: "MY DESK",
      to: ROUTER_PATH.myPage.desk,
    },
    {
      label: "MY INFORMATION",
      to: ROUTER_PATH.myPage.information,
    },
    ...(isAdmin
      ? [
          {
            label: "USER MANAGEMENT",
            to: ROUTER_PATH.user.dashboard._path,
          },
        ]
      : []),
    ...(isAdmin || isRegularMember
      ? [
          {
            label: "TAG MANAGEMENT",
            to: ROUTER_PATH.tag.dashboard._path,
          },
        ]
      : []),
    {
      label: "LOG OUT",
      onClick: () => setOpenLogoutModal(true),
    },
  ];

  /* 안 읽은 푸시 알림 */
  const { data: unreadPushNotificationCount } =
    useGetUnreadPushNotificationCount({
      referenceDate: CURRENT_DATE,
    });

  const unreadPush = unreadPushNotificationCount?.data.result.count;
  const hasNew = !!unreadPush;

  /* Drawer */
  const [openDrawer, setOpenDrawer] = useState(false);

  const menuList = useMemo(() => {
    return [
      ...(notNormalUser
        ? [
            {
              label: "DASHBOARD",
              to: ROUTER_PATH.home,
            },
            {
              label: "WORK",
              to: ROUTER_PATH.document.work.dashboard,
            },
            {
              label: "ANALYSIS",
              to: ROUTER_PATH.document.analysis.dashboard,
            },
          ]
        : []),
      {
        label: "ARCHIVES",
        to: ROUTER_PATH.document.archive.dashboard,
      },
      ...(notNormalUser
        ? [
            {
              label: "NOTICE",
              to: ROUTER_PATH.notice.dashboard._path,
            },
          ]
        : []),
      {
        label: (
          <>
            MY DESK
            {hasNewMyDeskInfo && <NewBadge />}
          </>
        ),
        to: `${ROUTER_PATH.myPage.desk}?selectedTab.uuid=${DOCUMENT_CATEGORY_UUID.WORK}&selectedTab.name=WORK&page=1`,
      },
      {
        label: "MY INFORMATION",
        to: ROUTER_PATH.myPage.information,
      },
      {
        label: (
          <>
            ALARM
            {hasNew && <NewBadge count={unreadPush} />}
          </>
        ),
        to: ROUTER_PATH.pushNotification._path,
      },
    ];
  }, [currentUser, hasNewMyDeskInfo]);

  /* 로그아웃 */
  const navigate = useNavigate();
  const logoutMutation = useLogoutMutation();
  const [openLogoutModal, setOpenLogoutModal] = useState(false);
  const setIsLoggedOut = useSetRecoilState(isLoggedOutState);

  const queryClient = useQueryClient();

  const onClickLogoutButton = () => {
    logoutMutation.mutate(
      {},
      {
        onSuccess: () => {
          logout();
          setIsLoggedOut(true);
          queryClient.removeQueries({ queryKey: api.user.getUserDetailKey() });
          navigate(ROUTER_PATH.auth.signIn);
        },
      },
    );
  };

  useEffect(() => {
    if (openDrawer) {
      document.body.style.overflow = "hidden"; // 스크롤 방지
    } else {
      document.body.style.overflow = ""; // 원래대로 복구
    }

    return () => {
      document.body.style.overflow = ""; // 컴포넌트 언마운트 시 복구
    };
  }, [openDrawer]);

  /* 탈퇴 모달 */
  const [isWithDrawModalOpen, setIsWithDrawModalOpen] = useState(false);

  /**
   * 정상 로그인 여부에 따라 홈화면 라우트 시키기 위한 useEffect
   */
  useEffect(() => {
    // 초기 로딩 중이 아닌데 현재 유저 데이터가 존재하지 않는 경우
    if (!isUserInitalLoading && !currentUser) {
      // 홈 화면으로 이동
      navigate(ROUTER_PATH.auth.signIn);
    }
  }, [isUserInitalLoading, currentUser, navigate]);

  return (
    <>
      <GnbHeaderMobileLayout>
        {showGnbWithTitle ? (
          <>
            <div className="GNB_title">
              <LeftIcon
                onClick={() =>
                  window.history.length > 1
                    ? navigate(-1)
                    : navigate(ROUTER_PATH.home)
                }
                style={{ height: "2.4rem" }}
              />
              <div className="title">
                {
                  GNB_WITH_TITLE_PATHS.find((path) =>
                    path.route.includes(location.pathname),
                  )?.title
                }
              </div>
            </div>
            <div className="GNB_icons">
              <AddFillIcon onClick={() => setOpenUploadBottomSheet(true)} />
              <GnbPushNotification
                onClick={() => navigate(ROUTER_PATH.pushNotification._path)}
                hasNew={unreadPushNotificationCount?.data.result.count ?? 0}
                iconSize={20}
              />
              <GnbItemProfile
                device="mobile"
                isNew={hasNewMyDeskInfo}
                onClick={() => setOpenUserBottomSheet(true)}
              />
              <HambugerIcon onClick={() => setOpenDrawer(true)} />
            </div>
          </>
        ) : showMobileGnb ? (
          <>
            <div className="GNB_title">
              <OCTOLogo
                width={50}
                onClick={() => {
                  if (match.pathname === ROUTER_PATH.home) {
                    window.location.reload();
                  } else {
                    navigate(ROUTER_PATH.home);
                  }
                }}
              />
              <Divider
                style={{
                  width: "1px",
                  height: "1rem",
                }}
              />
              <h1>{handle?.title}</h1>
            </div>
            <div className="GNB_icons">
              <AddFillIcon onClick={() => setOpenUploadBottomSheet(true)} />
              <GnbPushNotification
                onClick={() => navigate(ROUTER_PATH.pushNotification._path)}
                hasNew={unreadPushNotificationCount?.data.result.count ?? 0}
                iconSize={20}
              />
              <GnbItemProfile
                device="mobile"
                isNew={hasNewMyDeskInfo}
                onClick={() => setOpenUserBottomSheet(true)}
              />

              <HambugerIcon onClick={() => setOpenDrawer(true)} />
            </div>
          </>
        ) : (
          <>
            <LeftIcon
              onClick={() =>
                window.history.length > 1
                  ? navigate(-1)
                  : navigate(ROUTER_PATH.home)
              }
            />
          </>
        )}
      </GnbHeaderMobileLayout>

      <GnbDrawer open={openDrawer}>
        <header className="GNB_Drawer_header">
          <div className="GNB_Drawer_close">
            <CloseIcon onClick={() => setOpenDrawer(false)} />
          </div>
          {currentUser && (
            <div className="GNB_Drawer_name">
              <strong>{currentUser.name}</strong>님
            </div>
          )}
        </header>

        <Divider thickness={8} className="GNB_Drawer_header_divider" />

        <ul className="GNB_Drawer_menu">
          {menuList.map((menu) => (
            <li key={menu.to}>
              <Link to={menu.to} onClick={() => setOpenDrawer(false)}>
                {menu.label}
              </Link>
            </li>
          ))}
        </ul>

        <footer className="GNB_Drawer_footer">
          <div onClick={() => setOpenLogoutModal(true)}>LOGOUT</div>
          <Divider
            orientation="vertical"
            length={12}
            className="GNB_Drawer_footer_divider"
          />
          <div onClick={() => setIsWithDrawModalOpen(true)}>회원탈퇴</div>
        </footer>
      </GnbDrawer>

      <Modal.Root open={openLogoutModal} onOpenChange={setOpenLogoutModal}>
        <Modal.Body
          onConfirm={onClickLogoutButton}
          onCancel={() => setOpenLogoutModal(false)}
        >
          로그아웃하시겠습니까?
        </Modal.Body>
      </Modal.Root>

      <WithDrawModal
        isOpen={isWithDrawModalOpen}
        setIsOpen={setIsWithDrawModalOpen}
      />

      <BottomSheet
        isOpen={openUploadBottomSheet}
        setOpen={setOpenUploadBottomSheet}
        title="Upload"
      >
        {UPLOAD_OPTIONS.map(({ label, to }) =>
          to ? (
            <LinkStyle
              key={to}
              onClick={() => {
                setOpenUploadBottomSheet(false);
                setTimeout(() => navigate(to), 50);
              }}
            >
              {label}
            </LinkStyle>
          ) : (
            <DisabledLink key={label}>{label}</DisabledLink>
          ),
        )}
      </BottomSheet>

      <BottomSheet
        isOpen={openUserBottomSheet}
        setOpen={setOpenUserBottomSheet}
        title="USER"
      >
        {userMenu.map(({ label, to, onClick }) =>
          to ? (
            <LinkStyle
              key={to}
              onClick={() => {
                setOpenUserBottomSheet(false);
                setTimeout(() => navigate(to), 50);
              }}
            >
              {label}
            </LinkStyle>
          ) : (
            <div key={label} onClick={onClick}>
              {label}
            </div>
          ),
        )}
      </BottomSheet>
    </>
  );
};

export default GnbHeaderMobile;

const GnbHeaderMobileLayout = styled.div`
  background: ${({ theme }) => theme.color.background.white};
  padding: 1.1rem 1.6rem;
  display: flex;
  justify-content: space-between;
  position: sticky;
  top: 0;
  z-index: ${({ theme }) => theme.zIndex.mobileHeader};

  .GNB_title {
    display: flex;
    align-items: center;
    gap: 0.8rem;

    ${({ theme }) => theme.typography.body2.semibold};

    > svg {
      height: 1.2rem;
    }
  }

  .MYINFO_title {
    ${({ theme }) => theme.typography.body2.semibold}
    color: ${({ theme }) => theme.color.text.default}
  }

  .MYDESK_title {
    display: flex;
    align-items: center;
    gap: 0.8rem;
  }

  .GNB_icons {
    display: flex;
    align-items: center;
    gap: 1.2rem;
  }
`;

const GnbDrawer = styled.aside<{ open: boolean }>`
  background: ${({ theme }) => theme.color.background.white};

  width: 100vw;
  height: 100vh;
  position: fixed;
  top: 0;
  bottom: 0;
  left: ${({ open }) => (open ? 0 : "-100vw")};
  right: 0;
  z-index: ${({ theme }) => theme.zIndex.drawer};
  overflow: hidden;

  .GNB_Drawer_header {
    display: flex;
    flex-direction: column;
    padding: 0 1.6rem 2rem 1.6rem;
  }

  .GNB_Drawer_close {
    display: flex;
    justify-content: flex-end;
    padding: 1.3rem 0;
  }

  .GNB_Drawer_name {
    strong {
      ${({ theme }) => theme.typography.body1.medium}
    }

    ${({ theme }) => theme.typography.body3.regular}
  }

  .GNB_Drawer_header_divider {
    background-color: ${({ theme }) => theme.color.grey[100]};
  }

  .GNB_Drawer_menu {
    display: flex;
    flex-direction: column;
    padding: 0 1.6rem;

    li {
      height: 5.6rem;

      a {
        display: flex;
        align-items: center;
        gap: 1rem;

        width: 100%;
        height: 100%;

        ${({ theme }) => theme.typography.body1.regular}
      }

      &:not(:last-child) {
        border-bottom: 0.1rem solid ${({ theme }) => theme.color.stroke.light};
      }
    }
  }

  .GNB_Drawer_footer {
    display: flex;
    align-items: center;
    gap: 2rem;

    position: absolute;
    bottom: 0;
    margin: 0 1.6rem 4rem 1.6rem;

    div {
      ${({ theme }) => theme.typography.body3.regular}
      color: ${({ theme }) => theme.color.text.secondary};
    }
  }

  .GNB_Drawer_footer_divider {
    background: ${({ theme }) => theme.color.stroke.light};
  }
`;

const LinkStyle = styled.button`
  ${({ theme }) => theme.typography.body3.regular}
  cursor: pointer;
`;

const DisabledLink = styled.button`
  ${({ theme }) => theme.typography.body3.regular}
  color: ${({ theme }) => theme.color.text.disabled};
`;
